/* ═══════════════════════════════════════════════════════════════════════════
   Musique PROCÉDURALE, en couches (PLAN.md §5).

   Le plan est explicite : une musique layerée sur grille harmonique commune
   n'existe pas en CC0, et Suno/Udio sont juridiquement écartés. On la GÉNÈRE
   donc, comme tout le reste du projet — icônes PWA, silhouettes, planches
   d'explosion, bruitages.

   Le principe est celui de la musique adaptative : plusieurs STEMS (nappe, basse,
   arpège, batterie) partagent EXACTEMENT le même tempo, la même longueur de
   boucle et la même grille d'accords. Ils sont donc superposables dans n'importe
   quelle combinaison sans jamais se désaccorder. On monte ou descend l'intensité
   en fondant les couches, pas en changeant de morceau — ce qui évite toute
   couture audible. La synchronisation est gratuite : toutes les sources démarrent
   au même instant et bouclent sur la même durée, donc restent calées à jamais.

   Chaque stem est rendu UNE FOIS dans un `AudioBuffer` mono de la longueur EXACTE
   de la boucle, pour que `loop = true` se referme sans clic. Déterministe : tout
   aléa passe par un générateur seedé, deux joueurs entendent le même morceau.

   Grille : la mineur, progression Am–F–C–G (i–VI–III–VII), quatre mesures. Une
   couleur néon/spatiale simple et solide, qui ne fatigue pas en boucle.
   ═══════════════════════════════════════════════════════════════════════════ */

const BPM = 100;
const BEATS_PER_BAR = 4;
const BARS = 4;
const SEC_PER_BEAT = 60 / BPM; // 0,6 s
const SEC_PER_BAR = SEC_PER_BEAT * BEATS_PER_BAR; // 2,4 s
/** Longueur EXACTE de la boucle, en secondes. Toutes les couches la partagent. */
export const LOOP_SEC = SEC_PER_BAR * BARS; // 9,6 s

/** Les couches, de la plus calme à la plus intense. L'ordre définit l'empilement
 *  par niveau d'intensité (voir `MusicDirector`). */
export const MUSIC_STEMS = ['pad', 'bass', 'arp', 'drums'] as const;
export type StemId = (typeof MUSIC_STEMS)[number];

// Accords (Hz), une entrée par mesure. Notes d'accord pour la nappe et l'arpège.
const CHORDS: readonly (readonly number[])[] = [
  [220.0, 261.63, 329.63], // Am : A3 C4 E4
  [174.61, 220.0, 261.63], // F  : F3 A3 C4
  [261.63, 329.63, 392.0], // C  : C4 E4 G4
  [196.0, 246.94, 293.66], // G  : G3 B3 D4
];
// Fondamentales de basse (une octave plus bas), une par mesure.
const BASS_ROOT: readonly number[] = [110.0, 87.31, 130.81, 98.0]; // A2 F2 C3 G2

const TAU = Math.PI * 2;

function seeded(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let x = s;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** ADSR simple, normalisé sur [0, dur]. `s` = niveau de sustain. */
function adsr(t: number, dur: number, a: number, d: number, s: number, r: number): number {
  if (t < 0 || t > dur) return 0;
  if (t < a) return t / a;
  if (t < a + d) return 1 - (1 - s) * ((t - a) / d);
  if (t < dur - r) return s;
  return s * Math.max(0, 1 - (t - (dur - r)) / r);
}

/** Ajoute une note sinusoïdale (avec un léger dédoublement désaccordé pour la
 *  largeur) dans le tampon. Borne l'écriture à la fin du tampon. */
function addTone(
  data: Float32Array,
  rate: number,
  startSec: number,
  durSec: number,
  freq: number,
  amp: number,
  env: (t: number, dur: number) => number,
): void {
  const i0 = Math.floor(startSec * rate);
  const i1 = Math.min(data.length, Math.floor((startSec + durSec) * rate));
  const detune = freq * 1.004;
  for (let i = i0; i < i1; i++) {
    const t = (i - i0) / rate;
    const e = env(t, durSec);
    if (e <= 0) continue;
    const s = (Math.sin(TAU * freq * t) + Math.sin(TAU * detune * t) * 0.6) * 0.5;
    data[i]! += s * e * amp;
  }
}

/** Grosse caisse : sinus dont la hauteur chute vite — le corps d'un kick. */
function addKick(data: Float32Array, rate: number, startSec: number, amp: number): void {
  const dur = 0.22;
  const i0 = Math.floor(startSec * rate);
  const i1 = Math.min(data.length, Math.floor((startSec + dur) * rate));
  for (let i = i0; i < i1; i++) {
    const t = (i - i0) / rate;
    const f = 150 * Math.pow(0.28, t / dur) + 42;
    const e = Math.pow(1 - t / dur, 2.2);
    data[i]! += Math.sin(TAU * f * t) * e * amp;
  }
}

/** Bruit filtré court : charleston (aigu, très bref) ou caisse claire (corps
 *  tonal + bruit). `tone` > 0 ajoute une composante sinusoïdale (claire). */
function addNoise(
  data: Float32Array,
  rate: number,
  startSec: number,
  durSec: number,
  amp: number,
  rnd: () => number,
  tone = 0,
): void {
  const i0 = Math.floor(startSec * rate);
  const i1 = Math.min(data.length, Math.floor((startSec + durSec) * rate));
  for (let i = i0; i < i1; i++) {
    const t = (i - i0) / rate;
    const e = Math.pow(1 - t / durSec, 3);
    let v = (rnd() * 2 - 1) * e;
    if (tone > 0) v += Math.sin(TAU * tone * t) * e * 0.7;
    data[i]! += v * amp;
  }
}

const padEnv = (t: number, dur: number): number => adsr(t, dur, 0.12, 0.1, 0.85, 0.3);
const pluckEnv = (t: number, dur: number): number => adsr(t, dur, 0.003, dur, 0, 0);
const bassEnv = (t: number, dur: number): number => adsr(t, dur, 0.004, dur * 0.9, 0.2, 0.02);

function renderPad(data: Float32Array, rate: number): void {
  for (let bar = 0; bar < BARS; bar++) {
    const start = bar * SEC_PER_BAR;
    for (const f of CHORDS[bar]!) addTone(data, rate, start, SEC_PER_BAR, f, 0.05, padEnv);
  }
}

function renderBass(data: Float32Array, rate: number): void {
  // Une fondamentale par temps, avec un saut d'octave sur le 4e temps pour la
  // relance vers la mesure suivante.
  for (let bar = 0; bar < BARS; bar++) {
    const root = BASS_ROOT[bar]!;
    for (let beat = 0; beat < BEATS_PER_BAR; beat++) {
      const start = bar * SEC_PER_BAR + beat * SEC_PER_BEAT;
      const f = beat === 3 ? root * 2 : root;
      addTone(data, rate, start, SEC_PER_BEAT * 0.9, f, 0.16, bassEnv);
    }
  }
}

function renderArp(data: Float32Array, rate: number): void {
  // Croches, motif montant sur les notes de l'accord + l'octave, pluck brillant.
  const step = SEC_PER_BEAT / 2;
  for (let bar = 0; bar < BARS; bar++) {
    const c = CHORDS[bar]!;
    const seq = [c[0]!, c[1]!, c[2]!, c[0]! * 2, c[1]!, c[2]!, c[0]! * 2, c[2]!];
    for (let k = 0; k < seq.length; k++) {
      const start = bar * SEC_PER_BAR + k * step;
      addTone(data, rate, start, step * 1.1, seq[k]!, 0.07, pluckEnv);
    }
  }
}

function renderDrums(data: Float32Array, rate: number, rnd: () => number): void {
  for (let bar = 0; bar < BARS; bar++) {
    for (let beat = 0; beat < BEATS_PER_BAR; beat++) {
      const b = bar * SEC_PER_BAR + beat * SEC_PER_BEAT;
      // Kick sur 1 et 3, caisse claire sur 2 et 4.
      if (beat === 0 || beat === 2) addKick(data, rate, b, 0.5);
      if (beat === 1 || beat === 3) addNoise(data, rate, b, 0.14, 0.28, rnd, 190);
      // Charleston sur chaque croche, la contre-temps un peu plus douce.
      addNoise(data, rate, b, 0.03, 0.12, rnd);
      addNoise(data, rate, b + SEC_PER_BEAT / 2, 0.03, 0.08, rnd);
    }
  }
}

/** Rend toutes les couches dans des `AudioBuffer` mono de longueur `LOOP_SEC`.
 *  Appelé une fois, au déblocage audio, comme `synth.renderAll`. */
export function renderStems(ctx: BaseAudioContext): Map<StemId, AudioBuffer> {
  const rate = ctx.sampleRate;
  const n = Math.floor(LOOP_SEC * rate);
  const out = new Map<StemId, AudioBuffer>();

  for (const stem of MUSIC_STEMS) {
    const buf = ctx.createBuffer(1, n, rate);
    const data = buf.getChannelData(0);
    const rnd = seeded(stem.length * 2654435761 + n);
    if (stem === 'pad') renderPad(data, rate);
    else if (stem === 'bass') renderBass(data, rate);
    else if (stem === 'arp') renderArp(data, rate);
    else renderDrums(data, rate, rnd);

    // Sécurité anti-clip avant le limiteur : on borne franchement.
    for (let i = 0; i < n; i++) {
      const v = data[i]!;
      data[i] = v > 1 ? 1 : v < -1 ? -1 : v;
    }
    out.set(stem, buf);
  }
  return out;
}
